import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import ClassetDropdown from '../../../classetComponents/classetDropDown';
import ClassetCalendar from '../../../classetComponents/classetCalender';
import Service from '../../services';
import { baseUrlAdmin } from '../../../store/apiConstants';
import LoadingComponent from '../../loadingComponent';
import { format } from 'date-fns';
import '../transportStyles.scss';

class VehicleStaffAssignment extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            currentStaff: [],
            assignmentHistory: [],
            showAssignDialog: false,
            selectedStaffId: null,
            selectedRole: null,
            startDate: new Date(),
            staffDropdown: [],
            driversDropdown: [],
            attendersDropdown: [],
            roles: [
                { label: 'Driver', value: 'DRIVER' },
                { label: 'Attender', value: 'ATTENDER' }
            ],
            activeTab: 'current' // 'current' or 'history'
        };
        this.service = new Service();
        this.toast = React.createRef();
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.vehicleId !== this.props.vehicleId || prevProps.visible !== this.props.visible) {
            if (this.props.visible && this.props.vehicleId) {
                this.loadData();
            }
        }
    }

    loadData = async () => {
        await Promise.all([
            this.getCurrentStaff(),
            this.getAssignmentHistory(),
            this.getAvailableStaff()
        ]);
    };

    getCurrentStaff = async () => {
        const { vehicleId, branchId } = this.props;
        if (!vehicleId) return;

        this.setState({ isLoading: true });
        try {
            const url = `${baseUrlAdmin}/vehicle-staff-assignment/vehicle/${vehicleId}/current?branchId=${branchId}`;
            const res = await this.service.get(url, true);

            if (res?.res?.status && res.status) {
                this.setState({ currentStaff: res.res.data || [] });
            } else {
                this.toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load current staff assignments',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error loading current staff:', error);
            this.toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to load staff assignments',
                life: 3000
            });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    getAssignmentHistory = async () => {
        const { vehicleId, branchId } = this.props;
        if (!vehicleId) return;

        try {
            const url = `${baseUrlAdmin}/vehicle-staff-assignment/vehicle/${vehicleId}/history?branchId=${branchId}`;
            const res = await this.service.get(url, true);

            if (res?.res?.status && res.status) {
                this.setState({ assignmentHistory: res.res.data || [] });
            }
        } catch (error) {
            console.error('Error loading assignment history:', error);
        }
    };

    getAvailableStaff = async () => {
        const { branchId } = this.props;
        const academicYear = localStorage.getItem('userAcademicYear');

        try {
            const url = `${baseUrlAdmin}/transport-route/meta-data?academicYear=${academicYear}`;
            const payload = { branchId };
            const res = await this.service.post(url, payload, true);

            if (res?.res?.status && res.status) {
                const drivers = (res.res.data.drivers || []).map(staff => ({
                    label: `${staff.firstName} ${staff.lastName}`,
                    value: staff._id,
                    designation: 'DRIVER'
                }));

                const attenders = (res.res.data.attender || []).map(staff => ({
                    label: `${staff.firstName} ${staff.lastName}`,
                    value: staff._id,
                    designation: 'ATTENDER'
                }));

                this.setState({
                    driversDropdown: drivers,
                    attendersDropdown: attenders,
                    staffDropdown: [...drivers, ...attenders]
                });
            }
        } catch (error) {
            console.error('Error loading available staff:', error);
        }
    };

    handleAssignStaff = async () => {
        const { selectedStaffId, selectedRole, startDate } = this.state;
        const { vehicleId, branchId } = this.props;

        if (!selectedStaffId || !selectedRole) {
            this.toast.current?.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Please select staff member and role',
                life: 3000
            });
            return;
        }

        this.setState({ isLoading: true });
        try {
            const url = `${baseUrlAdmin}/vehicle-staff-assignment/assign`;
            const academicYear = localStorage.getItem('userAcademicYear');
            const payload = {
                vehicleId,
                staffId: selectedStaffId,
                role: selectedRole,
                branchId,
                academicYear,
                startDate: startDate || new Date()
            };

            const res = await this.service.post(url, payload, true);

            if (res?.res?.status && res.status) {
                this.toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Staff assigned successfully',
                    life: 3000
                });
                this.setState({
                    showAssignDialog: false,
                    selectedStaffId: null,
                    selectedRole: null,
                    startDate: new Date()
                });
                await this.loadData();
            } else {
                throw new Error(res?.res?.message || 'Assignment failed');
            }
        } catch (error) {
            this.toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to assign staff',
                life: 3000
            });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    handleRemoveStaff = (staffData) => {
        confirmDialog({
            message: `Are you sure you want to remove ${staffData.staffId.firstName} ${staffData.staffId.lastName} from this vehicle?`,
            header: 'Confirm Removal',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.removeStaff(staffData),
            reject: () => { }
        });
    };

    removeStaff = async (staffData) => {
        this.setState({ isLoading: true });
        try {
            const url = `${baseUrlAdmin}/vehicle-staff-assignment/remove`;
            const payload = {
                vehicleId: this.props.vehicleId,
                staffId: staffData.staffId._id,
                role: staffData.role
            };

            const res = await this.service.post(url, payload, true);

            if (res?.res?.status && res.status) {
                this.toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Staff removed successfully',
                    life: 3000
                });
                await this.loadData();
            } else {
                throw new Error(res?.res?.message || 'Removal failed');
            }
        } catch (error) {
            this.toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to remove staff',
                life: 3000
            });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    renderStaffName = (rowData) => {
        const staff = rowData.staffId;
        return staff ? `${staff.firstName} ${staff.lastName}` : 'N/A';
    };

    renderRole = (rowData) => {
        return <span className="p-tag p-tag-info">{rowData.role}</span>;
    };

    renderDate = (rowData, field) => {
        const date = rowData[field];
        return date ? format(new Date(date), 'dd MMM yyyy') : 'N/A';
    };

    renderStatus = (rowData) => {
        return (
            <span className={`p-tag ${rowData.isActive ? 'p-tag-success' : 'p-tag-secondary'}`}>
                {rowData.isActive ? 'Active' : 'Inactive'}
            </span>
        );
    };

    renderActions = (rowData) => {
        return rowData.isActive ? (
            <Button
                icon="pi pi-times"
                className="p-button-rounded p-button-danger p-button-sm"
                onClick={() => this.handleRemoveStaff(rowData)}
                tooltip="Remove Staff"
                tooltipOptions={{ position: 'left' }}
            />
        ) : null;
    };

    renderDialogFooter = () => {
        return (
            <div>
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    onClick={() => this.setState({ showAssignDialog: false })}
                    className="p-button-text"
                />
                <Button
                    label="Assign"
                    icon="pi pi-check"
                    onClick={this.handleAssignStaff}
                    autoFocus
                />
            </div>
        );
    };

    render() {
        const { visible, onHide, vehicleName } = this.props;
        const {
            isLoading,
            currentStaff,
            assignmentHistory,
            showAssignDialog,
            selectedStaffId,
            selectedRole,
            startDate,
            staffDropdown,
            roles,
            activeTab
        } = this.state;

        return (
            <>
                <Toast ref={this.toast} />
                <ConfirmDialog />

                <Dialog
                    visible={visible}
                    style={{ width: '90vw', maxWidth: '1200px' }}
                    header={`Manage Staff - ${vehicleName || 'Vehicle'}`}
                    modal
                    onHide={onHide}
                    maximizable
                >
                    {isLoading && <LoadingComponent />}

                    <div className="mb-4">
                        <div className="flex gap-2">
                            <Button
                                label="Assign New Staff"
                                icon="pi pi-plus"
                                onClick={() => this.setState({ showAssignDialog: true })}
                                className="p-button-success"
                            />
                            <Button
                                label="Current Assignments"
                                icon="pi pi-users"
                                onClick={() => this.setState({ activeTab: 'current' })}
                                className={activeTab === 'current' ? '' : 'p-button-outlined'}
                            />
                            <Button
                                label="Assignment History"
                                icon="pi pi-history"
                                onClick={() => this.setState({ activeTab: 'history' })}
                                className={activeTab === 'history' ? '' : 'p-button-outlined'}
                            />
                        </div>
                    </div>

                    {activeTab === 'current' ? (
                        <DataTable
                            value={currentStaff}
                            emptyMessage="No staff currently assigned"
                            showGridlines
                            className="transport-table-center"
                        >
                            <Column field="staffId" header="Staff Name" body={this.renderStaffName} style={{ width: '25%' }} />
                            <Column field="role" header="Role" body={this.renderRole} style={{ width: '20%' }} />
                            <Column field="startDate" header="Assigned Since" body={(rowData) => this.renderDate(rowData, 'startDate')} style={{ width: '25%' }} />
                            <Column field="isActive" header="Status" body={this.renderStatus} style={{ width: '20%' }} />
                            <Column header="Actions" body={this.renderActions} style={{ width: '10%' }} />
                        </DataTable>
                    ) : (
                        <DataTable
                            value={assignmentHistory}
                            emptyMessage="No assignment history"
                            showGridlines
                            className="transport-table-center"
                            paginator
                            rows={10}
                        >
                            <Column field="staffId" header="Staff Name" body={this.renderStaffName} style={{ width: '25%' }} />
                            <Column field="role" header="Role" body={this.renderRole} style={{ width: '15%' }} />
                            <Column field="startDate" header="Start Date" body={(rowData) => this.renderDate(rowData, 'startDate')} style={{ width: '20%' }} />
                            <Column field="endDate" header="End Date" body={(rowData) => this.renderDate(rowData, 'endDate')} style={{ width: '20%' }} />
                            <Column field="isActive" header="Status" body={this.renderStatus} style={{ width: '20%' }} />
                        </DataTable>
                    )}
                </Dialog>

                <Dialog
                    visible={showAssignDialog}
                    style={{ width: '450px' }}
                    header="Assign Staff to Vehicle"
                    modal
                    footer={this.renderDialogFooter()}
                    onHide={() => this.setState({ showAssignDialog: false, selectedRole: null, selectedStaffId: null })}
                >
                    <div className="p-fluid">
                        <div className="field mb-3">
                            <label htmlFor="role">Select Role *</label>
                            <ClassetDropdown
                                id="role"
                                optionLabel="label"
                                optionValue="value"
                                value={selectedRole}
                                options={roles}
                                onChange={(e) => this.setState({ selectedRole: e.value, selectedStaffId: null })}
                                placeholder="Select Role"
                            />
                        </div>

                        <div className="field mb-3">
                            <label htmlFor="staff">Select Staff Member *</label>
                            <ClassetDropdown
                                id="staff"
                                optionLabel="label"
                                optionValue="value"
                                value={selectedStaffId}
                                options={selectedRole === 'DRIVER' ? this.state.driversDropdown : selectedRole === 'ATTENDER' ? this.state.attendersDropdown : []}
                                onChange={(e) => this.setState({ selectedStaffId: e.value })}
                                placeholder={selectedRole ? "Select Staff" : "Select Role First"}
                                disabled={!selectedRole}
                            />
                        </div>

                        <div className="field mb-3">
                            <label htmlFor="startDate">Start Date</label>
                            <ClassetCalendar
                                id="startDate"
                                calendarMode="single"
                                value={startDate}
                                onChange={(e) => this.setState({ startDate: e.target.value })}
                                placeholder="Select Start Date"
                            />
                        </div>
                    </div>
                </Dialog>
            </>
        );
    }
}

export default VehicleStaffAssignment;
